import React, { useCallback, useEffect, useRef, useState } from "react";
import { buildApiUrl, getJsonAuthHeaders } from "../utils/utils";

export interface PickerPatient {
    patient_id: string;
    id?: string;
    first_name: string;
    last_name: string;
    middle_name?: string;
    email?: string;
    phone?: string;
}

interface PatientPickerProps {
    baseUrl: string;
    clinicId: string;
    /** Currently selected patient id (controlled). */
    value: string;
    onChange: (patientId: string, patient: PickerPatient | null) => void;
    placeholder?: string;
    /** Optional className applied to the visible input, defaults to the shared sc-input style. */
    inputClassName?: string;
    pageSize?: number;
    disabled?: boolean;
}

const PAGE_SIZE_DEFAULT = 20;
const DEBOUNCE_MS = 300;

const fullName = (patient: PickerPatient) =>
    [patient.first_name, patient.last_name].filter(Boolean).join(" ").trim() || patient.patient_id;

/**
 * Searchable, server-paginated patient picker. Instead of loading every patient
 * into a <select> (which does not scale past a few hundred rows), this queries
 * `facilities/patients/list` with a debounced search term and pages the results,
 * so it stays responsive even for clinics with tens of thousands of patients.
 */
const PatientPicker: React.FC<PatientPickerProps> = ({
    baseUrl,
    clinicId,
    value,
    onChange,
    placeholder = "Search patients by name, email, or ID",
    inputClassName = "sc-input",
    pageSize = PAGE_SIZE_DEFAULT,
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<PickerPatient[]>([]);
    const [selected, setSelected] = useState<PickerPatient | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const requestIdRef = useRef(0);

    const fetchPatients = useCallback(
        async (searchTerm: string, pageNum: number, append: boolean) => {
            if (!clinicId) return;
            const requestId = ++requestIdRef.current;
            setLoading(true);
            setError(null);
            try {
                const headers = await getJsonAuthHeaders();
                const url = buildApiUrl(
                    baseUrl,
                    `facilities/patients/list?clinic_id=${encodeURIComponent(clinicId)}` +
                        `&search=${encodeURIComponent(searchTerm)}` +
                        `&page=${pageNum}&page_size=${pageSize}`
                );
                const response = await fetch(url, { headers });
                const data = await response.json();
                // Ignore out-of-order responses (a newer keystroke already fired).
                if (requestId !== requestIdRef.current) return;
                const list: PickerPatient[] = Array.isArray(data?.data) ? data.data : [];
                setResults((prev) => (append ? [...prev, ...list] : list));
                setHasNext(Boolean(data?.pagination?.has_next));
                setPage(pageNum);
                if (data?.status && data.status !== "success") {
                    setError(data?.message || "Unable to load patients");
                }
            } catch (err) {
                if (requestId !== requestIdRef.current) return;
                setError("Unable to load patients. Please try again.");
                setResults([]);
                setHasNext(false);
            } finally {
                if (requestId === requestIdRef.current) setLoading(false);
            }
        },
        [baseUrl, clinicId, pageSize]
    );

    // Debounced search whenever the query changes while the dropdown is open.
    useEffect(() => {
        if (!open) return;
        const handle = window.setTimeout(() => {
            fetchPatients(query.trim(), 1, false);
        }, DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [query, open, fetchPatients]);

    // Keep local selection in sync when the parent clears/changes the value.
    useEffect(() => {
        if (!value) {
            setSelected(null);
        } else if (selected && selected.patient_id !== value) {
            setSelected(null);
        }
    }, [value, selected]);

    // Close the dropdown when clicking outside the component.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const openDropdown = () => {
        if (disabled) return;
        setOpen(true);
        if (results.length === 0) {
            fetchPatients("", 1, false);
        }
    };

    const handleSelect = (patient: PickerPatient) => {
        setSelected(patient);
        setQuery("");
        setOpen(false);
        onChange(patient.patient_id, patient);
    };

    const handleClear = () => {
        setSelected(null);
        setQuery("");
        onChange("", null);
        setOpen(false);
    };

    const displayValue = open ? query : selected ? fullName(selected) : "";

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <div style={{ position: "relative" }}>
                <input
                    className={inputClassName}
                    type="text"
                    value={displayValue}
                    placeholder={selected && !open ? fullName(selected) : placeholder}
                    disabled={disabled}
                    onFocus={openDropdown}
                    onClick={openDropdown}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        if (!open) setOpen(true);
                    }}
                    autoComplete="off"
                    aria-expanded={open}
                    role="combobox"
                />
                {selected && !open && (
                    <button
                        type="button"
                        onClick={handleClear}
                        aria-label="Clear selected patient"
                        style={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: 16,
                            lineHeight: 1,
                            color: "#6b7280",
                        }}
                    >
                        ×
                    </button>
                )}
            </div>

            {open && (
                <div
                    style={{
                        position: "absolute",
                        zIndex: 1000,
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        maxHeight: 280,
                        overflowY: "auto",
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                    }}
                >
                    {error && (
                        <div style={{ padding: "10px 12px", color: "#b91c1c", fontSize: 13 }}>{error}</div>
                    )}

                    {!error && results.length === 0 && !loading && (
                        <div style={{ padding: "10px 12px", color: "#6b7280", fontSize: 13 }}>
                            {query.trim() ? "No patients match your search." : "No patients enrolled yet."}
                        </div>
                    )}

                    {results.map((patient) => (
                        <button
                            key={patient.patient_id}
                            type="button"
                            onClick={() => handleSelect(patient)}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 2,
                                width: "100%",
                                textAlign: "left",
                                padding: "9px 12px",
                                border: "none",
                                borderBottom: "1px solid #f1f5f9",
                                background:
                                    selected && selected.patient_id === patient.patient_id ? "#f0fdfa" : "transparent",
                                cursor: "pointer",
                            }}
                        >
                            <span style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>{fullName(patient)}</span>
                            <span style={{ color: "#6b7280", fontSize: 12 }}>
                                {[patient.email, patient.patient_id].filter(Boolean).join(" · ")}
                            </span>
                        </button>
                    ))}

                    {loading && (
                        <div style={{ padding: "10px 12px", color: "#6b7280", fontSize: 13 }}>Loading…</div>
                    )}

                    {!loading && hasNext && (
                        <button
                            type="button"
                            onClick={() => fetchPatients(query.trim(), page + 1, true)}
                            style={{
                                width: "100%",
                                padding: "9px 12px",
                                border: "none",
                                background: "#f8fafc",
                                color: "#0f766e",
                                fontWeight: 600,
                                fontSize: 13,
                                cursor: "pointer",
                            }}
                        >
                            Load more results
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default PatientPicker;
